import { resolve } from "path";
import {
  DEFAULT_WAKE_RETRY_AFTER_SEC,
  isJobAdmissionKillSwitchActive,
  isWakeAppDeferredResult,
  readPositiveIntegerEnv,
  type WakeAppDeferredResult,
  type WakeAppResult,
} from "@gencow/core";
import {
  ensurePlatformRuntimeStateTable,
  getCurrentPlatformNodeId,
  getPlatformSlotActivity,
} from "./platform-active-slot";

export const CRON_POLL_INTERVAL = 60_000;
export const CRON_CONCURRENCY = 5;
export const CRON_EXEC_TIMEOUT = 60_000;
export const CRON_MAX_JOBS_PER_POLL = 50;
export const CRON_MAX_WAKE_PER_POLL = 3;
export const CRON_DUE_PER_APP_PER_POLL = 10;
export const CRON_EMPTY_LOG_INTERVAL = 5;
export const CRON_CONSECUTIVE_FAIL_THRESHOLD = 5;
export const CRON_INFLIGHT_RETRY_DELAY_SEC = 120;
export const DEFAULT_CRON_MIN_INTERVAL_SECONDS = 15 * 60;
export const APP_MEMORY_EXCEEDED_STATUS = "memory_exceeded";
export const APP_MEMORY_EXCEEDED_CODE = "APP_MEMORY_EXCEEDED";
export const CRON_MEMORY_EXCEEDED_LAST_STATUS = "blocked_memory_exceeded";

export interface PlatformCronJob {
  id: number;
  app_id: number;
  name: string;
  schedule: string;
  action: string;
  app_name: string;
  app_status: string;
  port: number;
}

interface LoggerLike {
  log(...args: unknown[]): void;
  warn(...args: unknown[]): void;
  error(...args: unknown[]): void;
}

interface ProvisionerCronBridge {
  generateCronToken(appDbId: number, appName: string): string;
  wakeApp(appName: string): Promise<WakeAppResult>;
  getAppPort(appName: string): number | undefined;
  provisionApp(
    appName: string,
    port: number,
    dataDir: string,
    appDbId: number,
    updateDbStatus?: boolean,
    reason?: string,
  ): Promise<void>;
  setAppMeta(appName: string, meta: { port: number; dataDir: string; appDbId: number }): void;
}

type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;

export class CronWakeDeferredError extends Error {
  readonly status: WakeAppDeferredResult["status"];
  readonly retryAfterSec: number;

  constructor(result: WakeAppDeferredResult) {
    super(`Cron wake deferred (${result.status})`);
    this.name = "CronWakeDeferredError";
    this.status = result.status;
    this.retryAfterSec = result.retryAfterSec || DEFAULT_WAKE_RETRY_AFTER_SEC;
  }
}

export function isCronWakeDeferredError(error: unknown): error is CronWakeDeferredError {
  return error instanceof CronWakeDeferredError;
}

export function calculateCronNextRun(schedule: string, currentDate = new Date()): Date {
  try {
    const { CronExpressionParser } = require("cron-parser");
    const interval = CronExpressionParser.parse(schedule, { currentDate });
    return interval.next().toDate();
  } catch {
    return new Date(currentDate.getTime() + 60_000);
  }
}

export function resolveCronMinimumIntervalSeconds(
  env: Record<string, string | undefined> = process.env,
): number {
  return readPositiveIntegerEnv(env, "GENCOW_CRON_MIN_INTERVAL_SECONDS") ?? DEFAULT_CRON_MIN_INTERVAL_SECONDS;
}

export function calculateCronAdmissionDeferUntil(
  schedule: string,
  options: {
    now?: Date;
    env?: Record<string, string | undefined>;
  } = {},
): Date {
  const now = options.now ?? new Date();
  const naturalNextRun = calculateCronNextRun(schedule, now);
  const minimumNextRun = new Date(now.getTime() + resolveCronMinimumIntervalSeconds(options.env) * 1000);
  return naturalNextRun.getTime() > minimumNextRun.getTime() ? naturalNextRun : minimumNextRun;
}

export function shouldExecuteCronJob(appStatus: string): boolean {
  return appStatus === "running" || appStatus === "idle";
}

export function splitCronExecutionJobs<T extends { app_name: string }>(
  jobs: T[],
  getPort: (appName: string) => number | undefined,
  maxWakePerPoll: number,
): { readyJobs: T[]; wakeJobs: T[]; deferredCount: number; executeJobs: T[] } {
  const readyJobs = jobs.filter((job) => getPort(job.app_name));
  const needWakeJobs = jobs.filter((job) => !getPort(job.app_name));
  const wakeJobs = needWakeJobs.slice(0, maxWakePerPoll);
  return {
    readyJobs,
    wakeJobs,
    deferredCount: needWakeJobs.length - wakeJobs.length,
    executeJobs: [...readyJobs, ...wakeJobs],
  };
}

export function resolveCronDuePerAppPerPoll(env: Record<string, string | undefined> = process.env): number {
  return (
    readPositiveIntegerEnv(env, "GENCOW_CRON_DUE_PER_APP_PER_POLL") ??
    readPositiveIntegerEnv(env, "GENCOW_CRON_DUE_PER_APP_PER_MINUTE") ??
    CRON_DUE_PER_APP_PER_POLL
  );
}

export function limitCronDueJobsPerApp<T extends { app_id: number }>(
  jobs: T[],
  maxPerApp: number,
): { allowed: T[]; deferred: T[] } {
  if (!Number.isInteger(maxPerApp) || maxPerApp <= 0) {
    return { allowed: jobs, deferred: [] };
  }
  const counts = new Map<number, number>();
  const allowed: T[] = [];
  const deferred: T[] = [];
  for (const job of jobs) {
    const nextCount = (counts.get(job.app_id) ?? 0) + 1;
    counts.set(job.app_id, nextCount);
    if (nextCount <= maxPerApp) {
      allowed.push(job);
    } else {
      deferred.push(job);
    }
  }
  return { allowed, deferred };
}

export function filterCronJobsByKillSwitch<
  T extends Pick<PlatformCronJob, "app_id" | "app_name" | "name" | "action">,
>(jobs: T[], env: Record<string, string | undefined> = process.env): { allowed: T[]; deferred: T[] } {
  const allowed: T[] = [];
  const deferred: T[] = [];
  for (const job of jobs) {
    const paused = [
      "cron:claim",
      `cron:claim:app:${job.app_id}`,
      `cron:claim:app-name:${job.app_name}`,
      `cron:claim:job:${job.name}`,
      `cron:claim:action:${job.action}`,
    ].some((scope) => isJobAdmissionKillSwitchActive(env, scope));
    if (paused) {
      deferred.push(job);
    } else {
      allowed.push(job);
    }
  }
  return { allowed, deferred };
}

export function buildCronDueJobsQuery(options: {
  nodeId?: string | null;
  limit?: number;
} = {}): string {
  const nodeFilter = options.nodeId ? "\n               AND a.node_id = $1" : "";
  const limit = options.limit ?? CRON_MAX_JOBS_PER_POLL;
  return `SELECT cj.id, cj.app_id, cj.name, cj.schedule, cj.action,
                    a.name AS app_name, a.status AS app_status, a.port
             FROM cron_jobs cj
             JOIN apps a ON a.id = cj.app_id
             WHERE cj.status = 'enabled'
               AND cj.next_run_at <= NOW()
               AND a.status IN ('running', 'idle')${nodeFilter}
             ORDER BY cj.next_run_at ASC
             LIMIT ${limit}`;
}

export async function markMemoryExceededCronJobs(params: {
  rawSql: RawSql;
  limit?: number;
  nodeId?: string | null;
  env?: Record<string, string | undefined>;
  log?: LoggerLike;
}): Promise<number> {
  const limit = Number.isInteger(params.limit) && params.limit! > 0 ? params.limit! : CRON_MAX_JOBS_PER_POLL;
  const nodeFilter = params.nodeId ? "\n        AND a.node_id = $2" : "";
  const queryParams = params.nodeId
    ? [APP_MEMORY_EXCEEDED_STATUS, params.nodeId]
    : [APP_MEMORY_EXCEEDED_STATUS];
  const jobs = (await params.rawSql(
    `SELECT cj.id, cj.name, cj.schedule, cj.action, a.name AS app_name
       FROM cron_jobs cj
       JOIN apps a ON a.id = cj.app_id
      WHERE cj.status = 'enabled'
        AND cj.next_run_at <= NOW()
        AND a.status = $1${nodeFilter}
      ORDER BY cj.next_run_at ASC
      LIMIT ${limit}`,
    queryParams,
  )) as Array<Pick<PlatformCronJob, "id" | "name" | "schedule" | "action" | "app_name">>;

  await Promise.all(
    jobs.map((job) =>
      params.rawSql(
        `UPDATE cron_jobs
            SET last_status = $1,
                last_error = $2,
                next_run_at = $3
          WHERE id = $4`,
        [
          CRON_MEMORY_EXCEEDED_LAST_STATUS,
          APP_MEMORY_EXCEEDED_CODE,
          calculateCronAdmissionDeferUntil(job.schedule, { env: params.env }),
          job.id,
        ],
      ),
    ),
  );
  if (jobs.length > 0) {
    params.log?.warn(`[cron] ${jobs.length} due job(s) blocked by memory_exceeded app status`);
  }
  return jobs.length;
}

export async function resolveCronAppPort(params: {
  job: Pick<PlatformCronJob, "app_name" | "app_status">;
  getPort: (appName: string) => number | undefined;
  wakeApp: (appName: string) => Promise<WakeAppResult>;
  provisionApp: ProvisionerCronBridge["provisionApp"];
  setAppMeta: ProvisionerCronBridge["setAppMeta"];
  rawSql: RawSql;
  log?: LoggerLike;
}): Promise<number> {
  const log = params.log ?? console;
  const currentPort = params.getPort(params.job.app_name);
  if (params.job.app_status !== "idle" && currentPort) {
    return currentPort;
  }

  const reason = params.job.app_status === "idle" ? "idle" : "port not in memory";
  const label = params.job.app_name;
  log.log(`[cron] ${label}: restoring (${reason})...`);
  const wakeResult = await params.wakeApp(params.job.app_name);
  let woke = wakeResult.ok;

  if (!wakeResult.ok && isWakeAppDeferredResult(wakeResult)) {
    log.warn(`[cron] ${label}: wake deferred (${wakeResult.status})`);
    throw new CronWakeDeferredError(wakeResult);
  }

  if (!woke) {
    log.warn(`[cron] ${label}: wakeApp failed — trying DB-based provision...`);
    try {
      const [appRow] = (await params.rawSql(
        `SELECT id, port FROM apps WHERE name = $1 AND status IN ('running','idle')`,
        [params.job.app_name],
      )) as Array<{ id: number; port: number }>;

      if (appRow) {
        const dataDir = `/var/gencow/deployments/${params.job.app_name}`;
        params.setAppMeta(params.job.app_name, { port: appRow.port, dataDir, appDbId: appRow.id });
        await params.provisionApp(params.job.app_name, appRow.port, dataDir, appRow.id, true, "wake");
        woke = true;
        log.log(`[cron] ${label}: DB-based provision success`);
      }
    } catch (provErr: unknown) {
      log.error(`[cron] ${label}: DB-based provision failed:`, (provErr as Error).message);
    }
  }

  if (!woke) {
    throw new Error(`Failed to restore app (${reason})`);
  }

  const restoredPort = params.getPort(params.job.app_name) ?? (wakeResult.ok ? wakeResult.port : undefined);
  if (!restoredPort) {
    throw new Error(`App port not found after restore attempt (status: ${params.job.app_status})`);
  }

  return restoredPort;
}

export async function executeCronJob(params: {
  job: PlatformCronJob;
  rawSql: RawSql;
  provisioner: ProvisionerCronBridge;
  cronExecTimeoutMs?: number;
  failCounts: Map<string, number>;
  consecutiveFailThreshold?: number;
  fetchImpl?: typeof fetch;
  log?: LoggerLike;
}): Promise<void> {
  const log = params.log ?? console;
  const fetchImpl = params.fetchImpl ?? fetch;
  const cronExecTimeoutMs = params.cronExecTimeoutMs ?? CRON_EXEC_TIMEOUT;
  const consecutiveFailThreshold = params.consecutiveFailThreshold ?? CRON_CONSECUTIVE_FAIL_THRESHOLD;
  const start = Date.now();
  const label = `${params.job.app_name}/${params.job.name}`;

  try {
    const port = await resolveCronAppPort({
      job: params.job,
      getPort: params.provisioner.getAppPort,
      wakeApp: params.provisioner.wakeApp,
      provisionApp: params.provisioner.provisionApp,
      setAppMeta: params.provisioner.setAppMeta,
      rawSql: params.rawSql,
      log,
    });

    const cronToken = params.provisioner.generateCronToken(params.job.app_id, params.job.app_name);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), cronExecTimeoutMs);

    try {
      const res = await fetchImpl(`http://localhost:${port}/internal/cron/${params.job.action}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Cron-Token": cronToken,
        },
        body: JSON.stringify({ action: params.job.action }),
        signal: controller.signal,
      });

      clearTimeout(timeout);

      const elapsed = ((Date.now() - start) / 1000).toFixed(1);
      if (res.ok) {
        const nextRun = calculateCronNextRun(params.job.schedule);
        await params.rawSql(
          `UPDATE cron_jobs SET last_run_at = NOW(), last_status = 'success', last_error = NULL, next_run_at = $1 WHERE id = $2`,
          [nextRun, params.job.id],
        );
        params
          .rawSql(
            `INSERT INTO cron_job_runs (cron_job_id, cron_name, action, status, duration_ms, started_at, completed_at) VALUES ($1, $2, $3, 'success', $4, $5, NOW())`,
            [
              params.job.id,
              params.job.name,
              params.job.action,
              Date.now() - start,
              new Date(start).toISOString(),
            ],
          )
          .catch(() => {});
        params.failCounts.delete(params.job.app_name);
        log.log(`[cron] ${label}: success (${elapsed}s)`);
        return;
      }

      const body = await res.text().catch(() => "");
      const nextRun = calculateCronNextRun(params.job.schedule);
      await params.rawSql(
        `UPDATE cron_jobs SET last_run_at = NOW(), last_status = 'failed', last_error = $1, next_run_at = $2 WHERE id = $3`,
        [body.slice(0, 500), nextRun, params.job.id],
      );
      params
        .rawSql(
          `INSERT INTO cron_job_runs (cron_job_id, cron_name, action, status, error, duration_ms, started_at, completed_at) VALUES ($1, $2, $3, 'failed', $4, $5, $6, NOW())`,
          [
            params.job.id,
            params.job.name,
            params.job.action,
            body.slice(0, 500),
            Date.now() - start,
            new Date(start).toISOString(),
          ],
        )
        .catch(() => {});
      log.error(`[cron] ${label}: failed (${elapsed}s) — ${res.status}`);
      params.failCounts.set(params.job.app_name, (params.failCounts.get(params.job.app_name) || 0) + 1);
    } catch (fetchErr: unknown) {
      clearTimeout(timeout);
      const elapsed = ((Date.now() - start) / 1000).toFixed(1);
      const isTimeout = (fetchErr as Error).name === "AbortError";
      const status = isTimeout ? "timeout" : "failed";
      const errMsg = isTimeout ? `Timeout after ${cronExecTimeoutMs / 1000}s` : (fetchErr as Error).message;
      const nextRun = calculateCronNextRun(params.job.schedule);
      await params.rawSql(
        `UPDATE cron_jobs SET last_run_at = NOW(), last_status = $1, last_error = $2, next_run_at = $3 WHERE id = $4`,
        [status, errMsg.slice(0, 500), nextRun, params.job.id],
      );
      params
        .rawSql(
          `INSERT INTO cron_job_runs (cron_job_id, cron_name, action, status, error, duration_ms, started_at, completed_at) VALUES ($1, $2, $3, $4, $5, $6, $7, NOW())`,
          [
            params.job.id,
            params.job.name,
            params.job.action,
            status,
            errMsg.slice(0, 500),
            Date.now() - start,
            new Date(start).toISOString(),
          ],
        )
        .catch(() => {});
      log.error(`[cron] ${label}: ${status} (${elapsed}s) — ${errMsg}`);
      params.failCounts.set(params.job.app_name, (params.failCounts.get(params.job.app_name) || 0) + 1);
    }
  } catch (err: unknown) {
    if (isCronWakeDeferredError(err)) {
      await params
        .rawSql(
          `UPDATE cron_jobs SET last_status = 'deferred', last_error = NULL, next_run_at = NOW() + make_interval(secs => $1) WHERE id = $2`,
          [err.retryAfterSec, params.job.id],
        )
        .catch(() => {});
      log.warn(`[cron] ${label}: wake deferred (${err.status}), retry in ${err.retryAfterSec}s`);
      return;
    }

    const elapsed = ((Date.now() - start) / 1000).toFixed(1);
    const errMsg = (err as Error).message;
    const nextRun = calculateCronNextRun(params.job.schedule);
    try {
      await params.rawSql(
        `UPDATE cron_jobs SET last_run_at = NOW(), last_status = 'failed', last_error = $1, next_run_at = $2 WHERE id = $3`,
        [errMsg.slice(0, 500), nextRun, params.job.id],
      );
      params
        .rawSql(
          `INSERT INTO cron_job_runs (cron_job_id, cron_name, action, status, error, duration_ms, started_at, completed_at) VALUES ($1, $2, $3, 'failed', $4, $5, $6, NOW())`,
          [
            params.job.id,
            params.job.name,
            params.job.action,
            errMsg.slice(0, 500),
            Date.now() - start,
            new Date(start).toISOString(),
          ],
        )
        .catch(() => {});
    } catch {
      /* ignore */
    }
    log.error(`[cron] ${label}: failed (${elapsed}s) — ${errMsg}`);

    const failCount = (params.failCounts.get(params.job.app_name) || 0) + 1;
    params.failCounts.set(params.job.app_name, failCount);
    if (failCount >= consecutiveFailThreshold && params.job.app_status === "running") {
      try {
        await params.rawSql(`UPDATE apps SET status = 'crashed' WHERE name = $1 AND status = 'running'`, [
          params.job.app_name,
        ]);
        log.log(
          `[cron] ${params.job.app_name}: ${failCount} consecutive failures -> DB status corrected to "crashed"`,
        );
        params.failCounts.delete(params.job.app_name);
      } catch {
        /* ignore */
      }
    }
  }
}

export async function registerPlatformCronScheduler(params: {
  functionsPath: string;
  rawSql: RawSql;
  fetchImpl?: typeof fetch;
  log?: LoggerLike;
  importProvisioner?: (path: string) => Promise<ProvisionerCronBridge>;
  setIntervalImpl?: typeof setInterval;
}): Promise<void> {
  const log = params.log ?? console;
  const importProvisioner =
    params.importProvisioner ?? ((path: string) => import(path) as Promise<ProvisionerCronBridge>);
  const setIntervalImpl = params.setIntervalImpl ?? setInterval;
  const provisioner = await importProvisioner(resolve(params.functionsPath, "../src/provisioner.ts"));
  const failCounts = new Map<string, number>();
  let emptyCount = 0;
  let lastActiveState: boolean | null = null;

  await ensurePlatformRuntimeStateTable(params.rawSql).catch((error: unknown) => {
    log.warn(`[platform] Platform Cron Scheduler active-slot table init failed: ${(error as Error).message}`);
  });

  setIntervalImpl(async () => {
    try {
      const activity = await getPlatformSlotActivity({ rawSql: params.rawSql, logger: log });
      if (!activity.active) {
        if (lastActiveState !== false) {
          log.log(
            `[platform] Platform Cron Scheduler paused on inactive slot :${activity.currentPort} (active :${activity.activePort})`,
          );
        }
        lastActiveState = false;
        return;
      }
      if (lastActiveState === false) {
        log.log(
          `[platform] Platform Cron Scheduler resumed on active slot :${activity.currentPort ?? "unknown"}`,
        );
      }
      lastActiveState = true;

      if (isJobAdmissionKillSwitchActive(process.env, "cron:claim")) {
        log.warn("[cron] Scheduler paused by job admission kill switch");
        return;
      }

      const currentNodeId = getCurrentPlatformNodeId();
      await markMemoryExceededCronJobs({
        rawSql: params.rawSql,
        nodeId: currentNodeId,
        env: process.env,
        log,
      }).catch((error: unknown) => {
        log.warn(
          `[cron] Failed to mark memory_exceeded jobs: ${
            error instanceof Error ? error.message : String(error)
          }`,
        );
      });
      const dueJobs = (await params.rawSql(
        buildCronDueJobsQuery({ nodeId: currentNodeId }),
        currentNodeId ? [currentNodeId] : undefined,
      )) as PlatformCronJob[];

      if (dueJobs.length === 0) {
        emptyCount++;
        if (emptyCount % CRON_EMPTY_LOG_INTERVAL === 0) {
          log.log(`[cron] No due jobs (${emptyCount} consecutive empty polls)`);
        }
        return;
      }
      emptyCount = 0;

      const killSwitchLimit = filterCronJobsByKillSwitch(dueJobs, process.env);
      if (killSwitchLimit.deferred.length > 0) {
        await Promise.all(
          killSwitchLimit.deferred.map((job) => {
            const nextRun = calculateCronAdmissionDeferUntil(job.schedule, { env: process.env });
            return params.rawSql(
              `UPDATE cron_jobs
                   SET last_status = 'deferred',
                       last_error = 'job_admission_disabled',
                       next_run_at = $1
                 WHERE id = $2`,
              [nextRun, job.id],
            );
          }),
        );
        log.warn(`[cron] ${killSwitchLimit.deferred.length} due job(s) deferred by kill switch`);
      }

      const dueLimit = limitCronDueJobsPerApp(killSwitchLimit.allowed, resolveCronDuePerAppPerPoll());
      if (dueLimit.deferred.length > 0) {
        await Promise.all(
          dueLimit.deferred.map((job) => {
            const nextRun = calculateCronAdmissionDeferUntil(job.schedule, { env: process.env });
            return params.rawSql(
              `UPDATE cron_jobs
                   SET last_status = 'deferred',
                       last_error = 'per_app_due_cap',
                       next_run_at = $1
                 WHERE id = $2`,
              [nextRun, job.id],
            );
          }),
        );
        log.warn(`[cron] ${dueLimit.deferred.length} due job(s) deferred by per-app poll cap`);
      }

      const { readyJobs, wakeJobs, deferredCount, executeJobs } = splitCronExecutionJobs(
        dueLimit.allowed,
        provisioner.getAppPort,
        CRON_MAX_WAKE_PER_POLL,
      );
      const needWakeCount = wakeJobs.length + deferredCount;

      if (deferredCount > 0) {
        log.warn(
          `[cron] ${needWakeCount} apps need wake, processing ${wakeJobs.length} this cycle (${deferredCount} deferred)`,
        );
      }

      log.log(
        `[cron] ${dueLimit.allowed.length} due - ${readyJobs.length} ready, ${wakeJobs.length} wake, ${deferredCount} deferred`,
      );

      if (executeJobs.length > 0) {
        await Promise.all(
          executeJobs.map((job) =>
            params.rawSql(
              `UPDATE cron_jobs
                   SET last_status = 'running',
                       last_error = NULL,
                       next_run_at = NOW() + make_interval(secs => $1)
                   WHERE id = $2`,
              [CRON_INFLIGHT_RETRY_DELAY_SEC, job.id],
            ),
          ),
        );

        void (async () => {
          for (let i = 0; i < executeJobs.length; i += CRON_CONCURRENCY) {
            const batch = executeJobs.slice(i, i + CRON_CONCURRENCY);
            await Promise.allSettled(
              batch.map((job) =>
                executeCronJob({
                  job,
                  rawSql: params.rawSql,
                  provisioner,
                  failCounts,
                  fetchImpl: params.fetchImpl,
                  log,
                }),
              ),
            );

            if (i + CRON_CONCURRENCY < executeJobs.length) {
              await new Promise((r) => setTimeout(r, 1000));
            }
          }
        })();
      }
    } catch (err: unknown) {
      log.error(`[cron] Scheduler error:`, (err as Error).message);
    }
  }, CRON_POLL_INTERVAL);

  log.log(
    `[platform] Platform Cron Scheduler registered (${CRON_POLL_INTERVAL / 1000}s poll, concurrency ${CRON_CONCURRENCY}, timeout ${CRON_EXEC_TIMEOUT / 1000}s)`,
  );
}
